import { MinorBadge } from "@/components/contractings/pills";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import dayjs from "dayjs";
import { toast } from "sonner";

import { capture } from "@/lib/posthog";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Building2,
  ChevronLeft,
  Users,
  Search,
  Eye,
  Loader2,
  Lock,
  X,
  Check,
} from "lucide-react";

import {
  useCompany,
  useCompanyContracts,
  useCompanyPriceItems,
  useCompanyNetwork,
} from "@/hooks/queries/useCompanies";
import { useUpdateContractingStatus } from "@/hooks/mutations/useContractingMutations";
import { ContractingUserStatus } from "@/lib/domain-constants";
import { formatNumber, formatMoney, formatMoneyCompact } from "@/lib/format";
import {
  GHOST_NOMENCLATURES,
  GHOST_DESCRIPTIONS,
  GHOST_CODES,
  GHOST_LOCATIONS,
  ghostPick,
} from "@/lib/ghost";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { StarButton } from "@/components/ui/StarButton";
import { Paginator } from "@/components/common/Paginator";
import {
  translateSubject,
  subjectClass,
} from "@/components/contractings/pills";
import { ContractingDetailSheet } from "@/components/contractings/ContractingDetailSheet";
import { CompanyNetworkGraph } from "@/components/companies/CompanyNetworkGraph";
import { CoactiveDebtSection } from "@/components/companies/CoactiveDebtSection";
import { useFollowedCompanies } from "@/hooks/queries/useFollowedCompanies";
import { useSuretyMode } from "@/hooks/useSuretyMode";
import {
  useToggleFollowCompany,
  useUnlockCompany,
} from "@/hooks/mutations/useCompanyMutations";
import type { CompanyContract, CompanyItem } from "@/types/domain";

const MONTHS_LONG = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];
const MONTHS_SHORT = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function formatReleaseDate(dateStr: string): string {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${parseInt(d!, 10)} de ${MONTHS_LONG[parseInt(m!, 10) - 1]} de ${y}`;
}

// ── OSCE / SUNAT extraction (mirrors the Angular getters) ──────────────────
function extractData(company?: CompanyItem) {
  const sunat = company?.company_extracteddata?.consultarucsunat;
  const osce = company?.company_extracteddata?.osce;
  const osceProveedor = osce?.basicInfo?.proveedorT01;
  const osceConformacion = osce?.resumen?.conformacion;
  const osceAntecedentes = osce?.resumen?.antecedentes;

  const gerenteOrgano = (osceConformacion?.organosAdm ?? []).find((o: any) =>
    o.descCargo?.toLowerCase().includes("gerente"),
  );
  const gerente: {
    nombre: string;
    documento: string;
    sigla: string;
  } | null = gerenteOrgano
    ? {
        nombre: gerenteOrgano.apellidosNomb ?? "",
        documento: gerenteOrgano.nroDocumento ?? "",
        sigla: gerenteOrgano.siglaDocIde ?? "",
      }
    : null;

  const socios: {
    nombre: string;
    porcentaje: number;
    documento: string;
    sigla: string;
    ruc: string;
  }[] = (osceConformacion?.socios ?? []).map((s: any) => ({
    nombre: s.razonSocial,
    porcentaje: s.porcentajeAcciones,
    documento: s.nroDocumento ?? "",
    sigla: s.siglaDocIde ?? "",
    // OSCE returns the string "null" when there is no RUC for the partner.
    ruc: s.numeroRuc && s.numeroRuc !== "null" ? s.numeroRuc : "",
  }));

  const actividades = (() => {
    const acts = sunat?.["Actividad(es) Económica(s)"];
    if (!acts) return [] as string[];
    return Array.isArray(acts) ? acts : [acts];
  })();

  const sinAntecedentes = (() => {
    const a = osceAntecedentes;
    if (!a) return true;
    return (
      (a.sanciones?.length ?? 0) === 0 &&
      (a.penalidades?.length ?? 0) === 0 &&
      (a.inhsJudicial?.length ?? 0) === 0 &&
      (a.inhsAdministrativa?.length ?? 0) === 0 &&
      (a.medidasCautelares?.length ?? 0) === 0
    );
  })();

  return {
    sunat,
    osce,
    osceProveedor,
    osceConformacion,
    gerente,
    socios,
    actividades,
    sinAntecedentes,
    hasExtractedData: !!(sunat?.wasOk || osce?.wasOk),
  };
}

function estadoClass(estado?: string): string {
  const e = (estado ?? "").toUpperCase();
  if (e === "ACTIVO")
    return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-300 dark:border-emerald-800";
  if (e === "BAJA")
    return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-300 dark:border-red-800";
  return "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700";
}
function condicionClass(cond?: string): string {
  if ((cond ?? "").toUpperCase() === "HABIDO")
    return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-300 dark:border-emerald-800";
  return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/20 dark:text-amber-300 dark:border-amber-800";
}

const CHIP =
  "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border";

/**
 * Nomenclatura de una licitación bloqueada: texto fantasma difuminado que actúa
 * como link al desbloqueo. El candado va sin blur —si no, nada indica que se
 * puede clickear— y el texto falso queda aria-hidden, con el nombre accesible
 * en el botón.
 */
function GhostNomenclatureLink({
  seed,
  ghosts,
  onClick,
}: {
  seed: number;
  ghosts: string[];
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Desbloquear licitación · 1 vista"
      title="Desbloquear licitación · 1 vista"
      className="group flex min-w-0 cursor-pointer items-center gap-1 text-left"
    >
      <Lock className="size-3 shrink-0 text-slate-400 group-hover:text-primary" />
      <span
        className="select-none truncate text-xs font-bold text-slate-400 blur-[4px]"
        aria-hidden="true"
      >
        {ghostPick(seed, ghosts)}
      </span>
    </button>
  );
}

function Card({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900",
        className,
      )}
    >
      {children}
    </div>
  );
}

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const companyId = Number(id);

  useEffect(() => {
    if (companyId) capture("company_viewed", { company_id: companyId });
  }, [companyId]);
  const navigate = useNavigate();
  const isSurety = useSuretyMode();
  // En Fianzas no existe el listado de Empresas: se vuelve a su cartera.
  const back = isSurety
    ? { path: "/surety/clients", label: "Volver a Clientes" }
    : { path: "/companies", label: "Volver a Empresas" };

  const { isLoading: companiesLoading, data: company } = useCompany(companyId);
  const { data: contracts = [] } = useCompanyContracts(companyId);
  // Fianzas no ve recursos: el backend le cierra esta ruta.
  const { data: priceItems = [] } = useCompanyPriceItems(companyId, !isSurety);
  const { data: network } = useCompanyNetwork(companyId);
  const saveMutation = useUpdateContractingStatus();
  const unlockCompany = useUnlockCompany();
  const toggleFollow = useToggleFollowCompany();
  const followed = useFollowedCompanies();
  const following = followed.isFollowing(companyId);

  // El modal necesita saber si está bloqueada para abrir compacto y evitar el
  // request que daría 403.
  const [selectedContract, setSelectedContract] = useState<{
    id: number;
    locked: boolean;
  } | null>(null);

  // Contracts pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Price-items search + pagination
  const [search, setSearch] = useState("");
  const [piPage, setPiPage] = useState(0);
  const [piPageSize, setPiPageSize] = useState(10);

  const data = useMemo(() => extractData(company), [company]);

  const topBuyers = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of contracts) {
      const name = c.entityName || "Entidad desconocida";
      map.set(name, (map.get(name) || 0) + (Number(c.amount) || 0));
    }
    const sorted = Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const max = sorted[0]?.[1] || 1;
    return sorted.map(([name, amount]) => ({
      name,
      amount,
      percentage: Math.round((amount / max) * 100),
    }));
  }, [contracts]);

  // Timeline: contracts with a release date, sorted chronologically.
  const timeline = useMemo(() => {
    const withDate = contracts.filter((c) => !!c.releaseDate);
    const sorted = [...withDate].sort((a, b) =>
      (a.releaseDate ?? "").localeCompare(b.releaseDate ?? ""),
    );
    return sorted.map((c) => {
      const [y, m, d] = (c.releaseDate ?? "").split("-");
      const month = `${MONTHS_SHORT[parseInt(m!, 10) - 1]} ${y}`;
      return {
        // Bloqueada, la fecha llega al mes: el día exacto la ubicaría en el OECE.
        label: c.isLocked ? month : `${d} ${month}`,
        amount: Math.round(Number(c.amount) || 0),
      };
    });
  }, [contracts]);

  const singleContract = useMemo(() => {
    const withDate = contracts.filter((c) => !!c.releaseDate);
    if (withDate.length !== 1) return null;
    return {
      date: withDate[0]!.releaseDate ?? "",
      amount: Number(withDate[0]!.amount) || 0,
    };
  }, [contracts]);

  const filteredPriceItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? priceItems.filter((i) => i.description.toLowerCase().includes(q))
      : priceItems;
  }, [priceItems, search]);

  const pagedContracts = contracts.slice(
    page * pageSize,
    page * pageSize + pageSize,
  );
  const pagedPriceItems = filteredPriceItems.slice(
    piPage * piPageSize,
    piPage * piPageSize + piPageSize,
  );

  const isSaved = (c: CompanyContract) =>
    c.contractinguser != null &&
    c.contractinguser.contractinguser_status !== null &&
    c.contractinguser.contractinguser_status !== undefined;

  function toggleSave(c: CompanyContract) {
    // The shared mutation handles the optimistic update across the
    // company-contracts cache and the global saved-count badge.
    saveMutation.mutate(
      {
        contracting_id: c.contracting_id,
        status: isSaved(c) ? null : ContractingUserStatus.SIN_ESTADO,
      },
      { onError: () => toast.error("No se pudo actualizar la licitación") },
    );
  }

  const openContract = useCallback(
    (contractingId: number, locked = false) =>
      setSelectedContract({ id: contractingId, locked }),
    [],
  );
  const openCompany = useCallback(
    (cid: number) => navigate(`/companies/${cid}`),
    [navigate],
  );
  const socios = data.socios;
  const gerente = data.gerente;

  const hasNetwork =
    !!network &&
    ((network.directContracts?.length ?? 0) > 0 ||
      (network.consortiums?.length ?? 0) > 0 ||
      socios.length > 0 ||
      !!gerente);

  if (companiesLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="size-10 animate-spin text-purple-600" />
          <span className="text-sm font-medium text-slate-400">
            Cargando empresa...
          </span>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Building2 className="mb-4 size-16 text-slate-300 dark:text-slate-700" />
        <h2 className="mb-1 text-lg font-bold text-slate-700 dark:text-slate-300">
          Empresa no encontrada
        </h2>
        <p className="mb-6 text-sm text-slate-400">
          No se pudo cargar la información de esta empresa.
        </p>
        <Button onClick={() => navigate(back.path)}>
          Volver al listado
        </Button>
      </div>
    );
  }

  const sunat = data.sunat;
  const osceProveedor = data.osceProveedor;
  const osceConformacion = data.osceConformacion;

  return (
    <div className="mx-auto mb-10 w-full flex-1 px-4 py-6 sm:px-18">
      {/* Back */}
      <div className="mb-6">
        <button
          onClick={() => navigate(back.path)}
          className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ChevronLeft className="size-4" />
          {back.label}
        </button>
      </div>

      {/* Header card */}
      <Card className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
              <Building2 className="size-7 text-slate-400 dark:text-slate-500" />
            </div>
            <div className="flex flex-col gap-1.5">
              {company.isLocked ? (
                <>
                  {/* Data fantasma: nombre, RUC y contacto son el lead. */}
                  <h1
                    aria-hidden
                    className="select-none text-2xl font-extrabold uppercase leading-tight text-slate-400 blur-[6px]"
                  >
                    Constructora Ejecutora SAC
                  </h1>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {company.department_name
                      ? `Gana sobre todo en ${company.department_name}. `
                      : ""}
                    Su nombre, RUC y contacto se ven al desbloquearla. No desbloquea sus
                    licitaciones.
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-2xl font-extrabold uppercase leading-tight text-slate-800 dark:text-slate-100">
                    {company.company_name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-blue-600 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white">
                      PE-RUC
                    </span>
                    <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                      {company.company_ruc}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          {company.isLocked ? (
            <button
              onClick={() => unlockCompany.mutate({ companyId })}
              disabled={unlockCompany.isPending}
              className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-wait disabled:opacity-70"
            >
              {unlockCompany.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Lock className="size-4" />
              )}
              Desbloquear empresa · 1 vista
            </button>
          ) : (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                onClick={() => toggleFollow.mutate({ companyId, follow: !following })}
                aria-pressed={following}
                className={cn(
                  "inline-flex cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold shadow-sm transition-colors",
                  following
                    ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800",
                )}
              >
                {following && <Check className="size-4" />}
                {following ? "Siguiendo" : "Seguir"}
              </button>
              <button
                onClick={() =>
                  window.open(
                    `https://apps.osce.gob.pe/perfilprov-ui/ficha/${company.company_ruc}`,
                    "_blank",
                  )
                }
                className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <Search className="size-4 text-slate-400" />
                Ver ficha OSCE
              </button>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="mt-5 flex flex-wrap items-center gap-8 border-t border-slate-100 pt-5 dark:border-slate-800">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              Adjudicados
            </span>
            <span className="text-3xl font-extrabold leading-none text-pink-500 dark:text-pink-400">
              {company.num_contracts}
            </span>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              procesos ganados
            </span>
          </div>
          <div className="h-10 w-px shrink-0 bg-slate-100 dark:bg-slate-800" />
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              Monto total adjudicado
            </span>
            <span className="text-3xl font-extrabold leading-none text-emerald-500 dark:text-emerald-400">
              {company.isLocked && "≈ "}
              {formatMoney(company.total_amount)}
            </span>
            <span className="text-xs text-slate-400 dark:text-slate-500">
              suma de contratos (Soles)
            </span>
          </div>
        </div>
      </Card>

      {/* OSCE / SUNAT */}
      {data.hasExtractedData && (
        <Card className="mb-5">
          <div className="mb-5 flex items-center gap-2">
            <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
              Ficha OSCE / SUNAT
            </h2>
            <div className="ml-2 flex flex-wrap items-center gap-1.5">
              {sunat?.["Estado del Contribuyente"] && (
                <span
                  className={cn(
                    CHIP,
                    estadoClass(sunat["Estado del Contribuyente"]),
                  )}
                >
                  {sunat["Estado del Contribuyente"]}
                </span>
              )}
              {sunat?.["Condición del Contribuyente"] && (
                <span
                  className={cn(
                    CHIP,
                    condicionClass(sunat["Condición del Contribuyente"]),
                  )}
                >
                  {sunat["Condición del Contribuyente"]}
                </span>
              )}
              {osceProveedor && (
                <span
                  className={cn(
                    CHIP,
                    osceProveedor.esAptoContratar
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-300 dark:border-emerald-800"
                      : "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-300 dark:border-red-800",
                  )}
                >
                  {osceProveedor.esAptoContratar
                    ? "Apto para contratar"
                    : "No apto para contratar"}
                </span>
              )}
              {data.sinAntecedentes && (
                <span
                  className={cn(
                    CHIP,
                    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-300 dark:border-emerald-800",
                  )}
                >
                  Sin antecedentes
                </span>
              )}
              {sunat?.coactiveDebt?.total > 0 && (
                <span
                  className={cn(
                    CHIP,
                    "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-300 dark:border-red-800",
                  )}
                >
                  Con deuda coactiva
                </span>
              )}
            </div>
            {sunat?.extracted_at && (
              <span className="ml-auto shrink-0 text-[11px] text-slate-400 dark:text-slate-500">
                SUNAT consultado el {dayjs(sunat.extracted_at).format("DD/MM/YYYY")}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Información general */}
            <div className="flex flex-col gap-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                Información general
              </p>
              <Field label="Tipo" value={sunat?.["Tipo Contribuyente"]} />
              <Field
                label="Domicilio fiscal"
                value={sunat?.["Domicilio Fiscal"]}
              />
              <Field
                label="Inscripción"
                value={sunat?.["Fecha de Inscripción"]}
              />
              <Field
                label="Inicio de actividades"
                value={sunat?.["Fecha de Inicio de Actividades"]}
              />
              {data.actividades.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Actividad(es) económica(s)
                  </span>
                  {data.actividades.map((act) => (
                    <span
                      key={act}
                      className="text-sm leading-snug text-slate-600 dark:text-slate-300"
                    >
                      {act}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Contacto y capacidad */}
            <div className="flex flex-col gap-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                Contacto y capacidad
              </p>
              {osceProveedor?.emails?.length > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Email(s)
                  </span>
                  {osceProveedor.emails.map((email: string) => (
                    <a
                      key={email}
                      href={`mailto:${email}`}
                      className="text-sm text-violet-600 hover:underline dark:text-violet-400"
                    >
                      {email}
                    </a>
                  ))}
                </div>
              )}
              {osceProveedor?.telefonos?.length > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Teléfono(s)
                  </span>
                  {osceProveedor.telefonos.map((tel: string) => (
                    <span
                      key={tel}
                      className="text-sm text-slate-600 dark:text-slate-300"
                    >
                      {tel}
                    </span>
                  ))}
                </div>
              )}
              {osceConformacion?.proveedor?.cmcTexto && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Capacidad máx. de contratación
                  </span>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    {osceConformacion.proveedor.cmcTexto}
                  </span>
                </div>
              )}
              {osceConformacion?.proveedor?.codigoRegistro && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Código de registro OSCE
                  </span>
                  <span className="font-mono text-sm text-slate-600 dark:text-slate-300">
                    {osceConformacion.proveedor.codigoRegistro}
                  </span>
                </div>
              )}
            </div>

            {/* Representantes y socios */}
            <div className="flex flex-col gap-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                Representantes y socios
              </p>
              {gerente && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Gerente general
                  </span>
                  <span className="text-sm font-semibold leading-snug text-slate-700 dark:text-slate-200">
                    {gerente.nombre}
                  </span>
                  {gerente.documento && (
                    <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                      {gerente.sigla || "Doc."} {gerente.documento}
                    </span>
                  )}
                </div>
              )}
              {socios.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    Accionistas
                  </span>
                  {socios.map((socio) => (
                    <div key={`${socio.nombre}-${socio.documento}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-slate-600 dark:text-slate-300">
                          {socio.nombre}
                        </span>
                        <span className="shrink-0 text-xs font-bold text-slate-700 dark:text-slate-200">
                          {socio.porcentaje}%
                        </span>
                      </div>
                      {(socio.ruc || socio.documento) && (
                        <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                          {socio.ruc
                            ? `RUC ${socio.ruc}`
                            : `${socio.sigla || "Doc."} ${socio.documento}`}
                        </span>
                      )}
                      <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-purple-600"
                          style={{ width: `${socio.porcentaje}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {sunat?.wasOk && (
            <CoactiveDebtSection coactiveDebt={sunat.coactiveDebt} />
          )}
        </Card>
      )}

      {/* Bloqueada: compradores y montos por obra llevarían al ganador. */}
      {company.isLocked && (
        <Card className="mb-5 flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
          <Lock className="size-4 shrink-0 text-slate-400" />
          Sus principales compradores y la evolución de lo que gana se ven al desbloquear la
          empresa.
        </Card>
      )}

      {/* Top buyers + timeline */}
      {!company.isLocked && (topBuyers.length > 0 || timeline.length > 0 || singleContract) && (
        <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {topBuyers.length > 0 && (
            <Card>
              <div className="mb-5 flex items-center justify-between">
                <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
                  Principales compradores
                </h2>
                <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">
                  Por monto contratado
                </span>
              </div>
              {topBuyers.length === 1 ? (
                <SingleMilestone
                  badge="Único comprador registrado"
                  title={topBuyers[0]!.name}
                  amount={formatMoneyCompact(topBuyers[0]!.amount)}
                  note="La comparativa aparecerá cuando haya más compradores."
                />
              ) : (
                <div className="flex flex-col gap-4">
                  {topBuyers.map((buyer) => (
                    <div key={buyer.name} className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <span className="truncate pr-4 text-sm font-medium text-slate-700 dark:text-slate-300">
                          {buyer.name}
                        </span>
                        <span className="shrink-0 text-sm font-bold text-slate-700 dark:text-slate-200">
                          {formatMoneyCompact(buyer.amount)}
                        </span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-purple-600 transition-all duration-700"
                          style={{ width: `${buyer.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {(timeline.length > 1 || singleContract) && (
            <Card>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
                  Evolución de contrataciones
                </h2>
                <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">
                  Por fecha de adjudicación
                </span>
              </div>
              {timeline.length > 1 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart
                    data={timeline}
                    margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="ts" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="0%"
                          stopColor="#8b5cf6"
                          stopOpacity={0.45}
                        />
                        <stop
                          offset="100%"
                          stopColor="#8b5cf6"
                          stopOpacity={0.02}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="4"
                      vertical={false}
                      stroke="#e2e8f0"
                      strokeOpacity={0.4}
                    />
                    <XAxis
                      dataKey="label"
                      angle={-35}
                      textAnchor="end"
                      height={60}
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tickFormatter={(v: number) => formatMoneyCompact(v)}
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      axisLine={false}
                      tickLine={false}
                      width={70}
                    />
                    <Tooltip
                      formatter={(value: any) =>
                        [
                          formatMoneyCompact(Number(value)),
                          "Monto adjudicado",
                        ] as [string, string]
                      }
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        background: "#1e293b",
                        color: "#fff",
                        fontSize: 12,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#8b5cf6"
                      strokeWidth={2.5}
                      fill="url(#ts)"
                      dot={{
                        r: 4,
                        fill: "#8b5cf6",
                        stroke: "#fff",
                        strokeWidth: 2,
                      }}
                      activeDot={{ r: 6 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : singleContract ? (
                <SingleMilestone
                  badge="Primer y único contrato registrado"
                  title={`Adjudicado el ${formatReleaseDate(singleContract.date)}`}
                  amount={formatMoneyCompact(singleContract.amount)}
                  note="El gráfico de evolución aparecerá cuando haya más contratos."
                />
              ) : null}
            </Card>
          )}
        </div>
      )}

      {/* Contracts + price items */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-7">
        {/* Licitaciones ganadas */}
        {contracts.length > 0 && (
          <div className="col-span-4 mb-5 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
              <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
                Licitaciones ganadas
              </h2>
              <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-600 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-400">
                {contracts.length}{" "}
                {contracts.length === 1 ? "contrato" : "contratos"}
              </span>
            </div>

            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                <div
                  className="grid gap-4 bg-slate-50 px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:bg-slate-800/50 dark:text-slate-500"
                  style={{
                    gridTemplateColumns:
                      "minmax(200px,1fr) 104px 130px 80px 120px 108px",
                  }}
                >
                  <div>Licitación</div>
                  <div>Fecha</div>
                  <div>Ubicación</div>
                  <div>Objeto</div>
                  <div>Monto</div>
                  <div />
                </div>

                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {pagedContracts.map((c) => (
                    <div
                      key={c.id}
                      className="grid items-center gap-4 px-6 py-3 text-sm transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/30"
                      style={{
                        gridTemplateColumns:
                          "minmax(200px,1fr) 104px 130px 80px 120px 108px",
                      }}
                    >
                      {c.isLocked ? (
                        <div className="flex min-w-0 flex-col">
                          <GhostNomenclatureLink
                            seed={c.contracting_id}
                            ghosts={GHOST_NOMENCLATURES}
                            onClick={() => openContract(c.contracting_id, true)}
                          />
                          <div
                            className="mt-1 line-clamp-2 select-none text-sm text-slate-300 blur-[4px] dark:text-slate-600"
                            aria-hidden="true"
                          >
                            {ghostPick(c.contracting_id, GHOST_DESCRIPTIONS)}
                          </div>
                        </div>
                      ) : (
                        <div className="flex min-w-0 flex-col">
                          <div className="truncate text-xs font-bold">
                            {/* inline: dentro de un truncate, un inline-block se
                                cortaría sin puntos suspensivos. */}
                            <button
                              type="button"
                              onClick={() => openContract(c.contracting_id)}
                              className="inline cursor-pointer hover:text-primary hover:underline"
                            >
                              {c.nomenclature}
                            </button>
                            {c.minorId ? <MinorBadge className="ml-1.5" /> : null}
                            {c.entityName ? ` · ${c.entityName}` : ""}
                          </div>
                          <div
                            className="mt-1 line-clamp-2 text-sm text-slate-400 dark:text-slate-500"
                            title={c.description ?? undefined}
                          >
                            {c.description}
                          </div>
                        </div>
                      )}
                      {/* Publicación y adjudicación. Bloqueada, el backend las
                          manda al mes: el día la ubicaría en el SEACE. */}
                      <div className="flex min-w-0 flex-col text-xs text-slate-600 dark:text-slate-300">
                        <span className="font-medium">
                          {contractDate(c.date, c.isLocked) ?? "-"}
                        </span>
                        {c.awardDate && (
                          <span className="text-slate-400">
                            Adjudicada {contractDate(c.awardDate, c.isLocked)}
                          </span>
                        )}
                      </div>
                      {/* Ubicación: provincia y distrito son data bloqueada
                          (el backend las anula) → se muestran difuminadas. */}
                      <div className="flex min-w-0 flex-col text-slate-600 dark:text-slate-300">
                        <div className="truncate font-medium">
                          {c.departmentName || "-"}
                        </div>
                        {c.isLocked ? (
                          <div
                            className="select-none truncate text-xs text-slate-400 blur-[3px]"
                            aria-hidden="true"
                          >
                            {ghostPick(c.contracting_id, GHOST_LOCATIONS)}
                          </div>
                        ) : (
                          (c.provinceName || c.districtName) && (
                            <div className="truncate text-xs text-slate-400">
                              {[c.provinceName, c.districtName]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          )
                        )}
                      </div>
                      <div className="flex items-center">
                        {c.contractSubject && (
                          <span
                            className={cn(
                              "rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                              subjectClass(c.contractSubject),
                            )}
                          >
                            {translateSubject(c.contractSubject)}
                          </span>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <div className="truncate font-bold">
                          {c.amount ? formatMoney(c.amount) : "-"}
                        </div>
                        {c.matchedItems?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {c.matchedItems.map((item) => (
                              <span
                                key={item.description}
                                title={item.description}
                                className="inline-flex max-w-[250px] items-center truncate rounded-lg bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400"
                              >
                                <span className="mr-1 size-1 shrink-0 rounded-full bg-emerald-500" />
                                <span className="truncate font-bold">
                                  {item.description}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Bloqueada: no se puede ver ni guardar lo que aún no
                          se desbloqueó — solo el CTA de desbloqueo. */}
                      {c.isLocked ? (
                        <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => openContract(c.contracting_id, true)}
                            title="Desbloquear oportunidad (1 vista)"
                            className="flex cursor-pointer items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[11px] font-bold text-primary transition-colors hover:bg-primary/20"
                          >
                            <Lock className="size-3.5" />
                            Ver
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <StarButton
                            saved={isSaved(c)}
                            onClick={() => toggleSave(c)}
                            disabled={saveMutation.isPending}
                            title={
                              isSaved(c)
                                ? "Quitar de mis licitaciones"
                                : "Guardar licitación"
                            }
                          />
                          <button
                            onClick={() => openContract(c.contracting_id)}
                            title="Ver detalle"
                            className="flex size-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                          >
                            <Eye className="size-4" />
                          </button>
                        </div>
                      )}
                      {c.consortium && (
                        <ConsortiumLine consortium={c.consortium} />
                      )}
                    </div>
                  ))}
                </div>

                <Paginator
                  page={page}
                  pageSize={pageSize}
                  total={contracts.length}
                  pageSizeOptions={[5, 10, 25, 100]}
                  onPageChange={setPage}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setPage(0);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Catálogo de ítems */}
        {priceItems.length > 0 && (
          <div className="col-span-3 mb-5 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-6 py-4 dark:border-slate-800">
              <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
                Catálogo de ítems adquiridos
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-600 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-400">
                  {priceItems.length} ítems
                </span>
                <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 dark:border-slate-700 dark:bg-slate-800">
                  <Search className="size-4 shrink-0 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPiPage(0);
                    }}
                    placeholder="Buscar ítem..."
                    className="w-48 border-none bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-200"
                  />
                  {search && (
                    <button
                      onClick={() => {
                        setSearch("");
                        setPiPage(0);
                      }}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_90px_90px_130px] gap-4 bg-slate-50 px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:bg-slate-800/50 dark:text-slate-500">
              <span>Descripción</span>
              <span className="text-right">Medida</span>
              <span className="text-right">Cantidad</span>
              <span className="text-right">Monto total</span>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {pagedPriceItems.map((item, i) => (
                <div
                  key={`${item.description}-${i}`}
                  className="grid grid-cols-[1fr_90px_90px_130px] items-center gap-4 px-6 py-3 transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/30"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span
                      className="line-clamp-2 text-sm leading-snug text-slate-700 dark:text-slate-200"
                      title={item.description}
                    >
                      {item.description}
                    </span>
                    {/* La nomenclatura (real o fantasma) es el acceso a la
                        licitación de origen: desbloquear el ítem que interesa
                        sin tener que adivinar de cuál viene. */}
                    {item.isLocked ? (
                      <GhostNomenclatureLink
                        seed={item.contracting_id}
                        ghosts={GHOST_CODES}
                        onClick={() => openContract(item.contracting_id, true)}
                      />
                    ) : (
                      item.nomenclature && (
                        <button
                          type="button"
                          onClick={() => openContract(item.contracting_id)}
                          title={item.nomenclature}
                          className="w-fit max-w-full cursor-pointer truncate font-mono text-[11px] text-slate-400 hover:text-primary hover:underline dark:text-slate-500"
                        >
                          {item.nomenclature}
                        </button>
                      )
                    )}
                  </div>
                  <div className="text-right text-sm text-slate-500 dark:text-slate-400">
                    {item.unit || "—"}
                  </div>
                  <div className="text-right font-mono text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {item.quantity != null ? formatNumber(item.quantity) : "—"}
                  </div>
                  <div className="text-right font-mono text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    {item.total_amount > 0
                      ? formatMoney(item.total_amount)
                      : "—"}
                  </div>
                </div>
              ))}

              {filteredPriceItems.length === 0 && (
                <div className="px-6 py-10 text-center text-sm text-slate-400 dark:text-slate-500">
                  No se encontraron ítems para <strong>{search}</strong>
                </div>
              )}
            </div>

            <Paginator
              page={piPage}
              pageSize={piPageSize}
              total={filteredPriceItems.length}
              pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={setPiPage}
              onPageSizeChange={(s) => {
                setPiPageSize(s);
                setPiPage(0);
              }}
            />
          </div>
        )}
      </div>

      {/* Red de socios */}
      {hasNetwork && network && (
        <Card>
          <div className="mb-4 flex items-center gap-2">
            <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">
              Red de socios
            </h2>
            <span className="ml-auto rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-600 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-400">
              {network.consortiums?.length ?? 0}{" "}
              {network.consortiums?.length === 1 ? "consorcio" : "consorcios"}
            </span>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-5">
            <LegendDot color="#b07b1e" label="Foco" />
            <LegendDot color="#2f80ed" label="Proyecto" />
            <LegendDot color="#94a3b8" label="Bloqueada" />
            <LegendDot color="#0f2742" label="Empresa" />
            <LegendDot color="#2e7d6b" label="Contacto" />
          </div>
          <CompanyNetworkGraph
            companyId={companyId}
            companyName={company.company_name ?? ""}
            network={network}
            gerente={gerente?.nombre ?? ""}
            socios={socios}
            onOpenContract={openContract}
            onOpenCompany={openCompany}
          />
          <p className="mt-2 text-center text-xs text-slate-400 dark:text-slate-500">
            Podés arrastrar y hacer zoom. El grosor de línea indica cantidad de
            contratos compartidos.
          </p>
        </Card>
      )}

      <ContractingDetailSheet
        contractingId={selectedContract?.id ?? null}
        locked={selectedContract?.locked}
        open={selectedContract != null}
        onOpenChange={(o) => !o && setSelectedContract(null)}
      />
    </div>
  );
}

// ── Small presentational helpers ───────────────────────────────────────────

function Field({
  label,
  value,
  bold,
}: {
  label: string;
  value?: string | null;
  bold?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-slate-400 dark:text-slate-500">
        {label}
      </span>
      <span
        className={cn(
          "text-sm leading-snug text-slate-600 dark:text-slate-300",
          bold && "font-semibold text-slate-700 dark:text-slate-200",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function SingleMilestone({
  badge,
  title,
  amount,
  note,
}: {
  badge: string;
  title: string;
  amount: string;
  note: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-5 py-4">
      <div className="flex w-full max-w-xs flex-col items-center gap-1">
        <div className="h-8 w-px bg-gradient-to-b from-transparent to-violet-400" />
        <div className="size-4 rounded-full bg-violet-500 shadow-lg shadow-violet-400/30 ring-4 ring-violet-200 dark:ring-violet-900" />
        <div className="h-8 w-px bg-gradient-to-b from-violet-400 to-transparent" />
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-600 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-400">
          <span className="size-1.5 rounded-full bg-violet-500" />
          {badge}
        </span>
        <p className="max-w-[240px] text-base font-bold leading-snug text-slate-800 dark:text-slate-100">
          {title}
        </p>
        <p className="text-2xl font-extrabold text-emerald-500 dark:text-emerald-400">
          {amount}
        </p>
      </div>
      <p className="text-xs italic text-slate-300 dark:text-slate-600">
        {note}
      </p>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="size-3 shrink-0 rounded-full"
        style={{ background: color }}
      />
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {label}
      </span>
    </div>
  );
}

/** "12 oct 2026"; si la licitación está bloqueada el backend manda el mes, y eso se muestra. */
function contractDate(date: string | null | undefined, isLocked?: boolean) {
  if (!date) return null;
  return dayjs(date).format(isLocked ? "MMM YYYY" : "DD MMM YYYY");
}

/**
 * Con quién ganó en consorcio, debajo de la fila. Cada socio se ve según su propio
 * desbloqueo, como en el grafo de la empresa.
 */
function ConsortiumLine({
  consortium,
}: {
  consortium: NonNullable<CompanyContract["consortium"]>;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400"
      style={{ gridColumn: "1 / -1" }}
    >
      <Users className="size-3.5 shrink-0 text-slate-400" />
      <span className="font-semibold text-slate-600 dark:text-slate-300">
        {consortium.name ?? "En consorcio"}
      </span>
      {consortium.members.length > 0 && <span>· con</span>}
      {consortium.members.map((member, index) => (
        <span key={member.companyId} className="inline-flex items-center">
          {member.isLocked ? (
            <span className="select-none blur-[3px]" aria-label="Socio bloqueado">
              EMPRESA SOCIA S.A.C.
            </span>
          ) : (
            <Link
              to={`/companies/${member.companyId}`}
              className="cursor-pointer font-medium text-slate-600 hover:text-primary hover:underline dark:text-slate-300"
            >
              {member.name}
            </Link>
          )}
          {index < consortium.members.length - 1 && ","}
        </span>
      ))}
    </div>
  );
}
